import { IAddOn } from './mock-listings.data';

/**
 * `IListingExtra.label` is a reserved TYPE key for the few extras the server
 * populates with no guest-facing `name`/`description` at all (its entity doc
 * says "Not used for extraVehicle or extraTent"), so those need display text
 * from somewhere. This table is that somewhere.
 *
 * It lives here rather than in either mapper because BOTH directions need it —
 * the guest read path and the host edit round-trip — and `listing-entity.mapper`
 * already imports from `draft-listing-real-mapper`, so putting it in either one
 * would make the pair circular.
 */
export const RESERVED_EXTRA_LABELS: Record<string, string> = {
  extraParking: 'Extra Vehicles',
  extraTent: 'Tents',
  firewood: 'Firewood',
};

/** The inverse: host-visible text back to the server's type key, lowercased so
 *  "Firewood" and "firewood" land on the same one. */
export const RESERVED_ADDON_TYPE_KEYS: Record<string, string> = {
  'extra vehicles': 'extraParking',
  tents: 'extraTent',
  firewood: 'firewood',
};

/**
 * Guest-facing name for a real extra, in priority order: the `name` column the
 * server documents as "A name shown to guests", then the reserved lookup for
 * rows that have no name, then `label` as a last resort — which only matters
 * for rows written before the publish path was writing `name` at all.
 *
 * Sentence-cased, not title-cased (Batch A #6). The reported defect is a
 * lowercase `tour` beside `Firewood`; capitalising the first letter fixes
 * exactly that and leaves the rest of the host's text alone. Title case would
 * turn "BBQ propane" into "Bbq Propane" and "ATV rental" into "Atv Rental",
 * both plausible names here — and the host editor's own starter templates
 * ("Firewood bundle", "Late check-out") are sentence case, so this is the
 * house style hosts are already being shown.
 */
export function addOnDisplayName(e: { label?: string; name?: string }): string {
  const raw = e.name?.trim() || (e.label && RESERVED_EXTRA_LABELS[e.label]) || e.label?.trim() || '';
  if (!raw) return 'Add-on';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * The unit a real extra is charged in.
 *
 * The server has no `unit` column, so this is derived rather than stored. A
 * `count` cap above 1 means the guest may buy several at the stated price, which
 * is exactly the "per vehicle or per stay?" question Batch A #6 raised —
 * listing 38 caps Extra Vehicles at 2. Anything else is a flat charge for the
 * stay.
 *
 * CONSEQUENCE, and it needs one column server-side to fix properly: a host who
 * picks "per night" or "per person" in the editor cannot have that persisted,
 * so it is lost at publish. See the fixes doc.
 */
export function addOnUnitFromCount(count: number | undefined): IAddOn['unit'] {
  return typeof count === 'number' && count > 1 ? 'per unit' : 'per stay';
}

/** A real cap, or undefined when the row states none. */
export function addOnCapFromCount(count: number | undefined): number | undefined {
  return typeof count === 'number' && count > 0 ? count : undefined;
}

/**
 * The one place an add-on's price is turned into money, and the one place its
 * unit is turned into words.
 *
 * Both of these were written out more than once — the line maths in
 * `BookingStateService` (`addOnsTotal`, `addOnLineTotal`) and again in
 * `BookingReviewComponent` (`addOnAmount`), and the unit suffix in four
 * templates. Two copies of a total that must agree is a latent defect: the
 * listing page and the review page could quote different figures for the same
 * selection and nothing would catch it. Centralising is the guard.
 */

/** What one selected add-on costs, given the stay it is attached to. */
export function addOnLineAmount(
  addOn: Pick<IAddOn, 'price' | 'unit'>,
  ctx: { nights: number; guests: number; quantity: number },
): number {
  switch (addOn.unit) {
    // `nights || 1` throughout: a quote before dates are picked still has to
    // show a number rather than collapse the line to zero.
    case 'per night':
      return addOn.price * (ctx.nights || 1);
    case 'per person':
      return addOn.price * (ctx.guests || 1);
    case 'per unit':
      return addOn.price * (ctx.quantity || 1);
    default:
      return addOn.price;
  }
}

/**
 * The suffix after the price, e.g. "$5 / night".
 *
 * 'per stay' reads "/ stay" rather than being left bare so the guest is told
 * the basis explicitly — "Extra Vehicles $1" alone was the ambiguity reported
 * in Batch A #6, where nothing said whether the charge was per vehicle or for
 * the whole stay.
 */
export function addOnUnitSuffix(unit: IAddOn['unit']): string {
  switch (unit) {
    case 'per night':
      return 'night';
    case 'per person':
      return 'person';
    case 'per unit':
      return 'each';
    default:
      return 'stay';
  }
}

/**
 * Upper bound for the quantity stepper. The server's `count` is "the maximum
 * number of the extra that can be purchased with the reservation", so it is a
 * real cap and must be honoured; 99 is only a sanity ceiling for add-ons that
 * state none.
 */
export function addOnMaxQuantity(addOn: Pick<IAddOn, 'maxQuantity'>): number {
  const cap = addOn.maxQuantity;
  return typeof cap === 'number' && cap > 0 ? cap : 99;
}
